import { Component } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { MeasuresFacade } from '@library/store/measures/measures.facade';
import { EnumMeasureTypes } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { Module, SourceType } from '@library/store/modules/modules.interface';
import { RoomsFacade } from '@library/store/rooms/rooms.facade';
import { Room } from '@library/store/rooms/rooms.interface';
import { PowerLines } from '@library/utils/constants/power-lines';
import { Gradient } from '@library/utils/helpers/functions/consumption/color-gradient';
import { ConsumptionGroup } from '../control-page-footer-consumption.component';
import { ControlPageFooterPowerlinesService } from './control-page-footer-powerlines.service';
import { ConsumptionUnit, ControlPageState, ControlPageStore } from '@pages/control-page/control-page-store.service';
import { combineLatest, combineLatestWith, map, Observable, of, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs';

@Component({
  selector: 'app-control-page-footer-powerlines',
  templateUrl: './control-page-footer-powerlines.component.html',
  styleUrls: ['./control-page-footer-powerlines.component.scss'],
  providers: [ControlPageFooterPowerlinesService],
})
export class ControlPageFooterPowerlinesComponent {

  selectedGroup$ = this.selectedPowerlinesGroup.selectedGroup$;

  private data$ = combineLatest({
    state: this.controlPageStore.state$,
    modules: this.modulesFacade.powerLines$,
    rooms: this.roomsFacade.currentHomeRooms$,
  });

  powerlinesGroups$: Observable<ConsumptionGroup[]> = this.data$.pipe(
    this.combineModulesRoomsAndTotals(),
    this.groupBySourceType(),
    this.combineWithHomeTotal(),
    this.addRestOfHome(),
    this.addGroupTotalAndRatio(),
    this.sortByValue(),
    this.addColors(),
    this.updateSelectedGroup(),
    shareReplay(1),
  );

  constructor(
    private controlPageStore: ControlPageStore,
    private modulesFacade: ModulesFacade,
    private homesFacade: HomesFacade,
    private roomsFacade: RoomsFacade,
    private measuresFacade: MeasuresFacade,
    private selectedPowerlinesGroup: ControlPageFooterPowerlinesService,
  ) { }

  /**
   * Gets the totals for each module and associate it with it's room
   */
  private combineModulesRoomsAndTotals() {
    return (source$: Observable<{modules: Module[], state: ControlPageState, rooms: Room[]}>) => {
      return source$.pipe(
        switchMap(({modules, state, rooms}) => {
          if (modules.length === 0) return of([]);
          return combineLatest(modules.map(module => this.measuresFacade.getModuleTotal(
            module.id,
            state.homeConsumptionUnit === ConsumptionUnit.CONSUMPTION
              ? EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE
              : EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
            this.controlPageStore.scaleToApiScale(state.scale),
            state.timeRange,
          ).pipe(
            map(value => ({module, value, room: rooms.find(r => r.id === module.room_id)})),
          )));
        }),
      );
    };
  }

  /**
   * Group modules by their appliance type
   */
  private groupBySourceType() {
    return (source$: Observable<{module: Module, value: number, room: Room}[]>) => {
      return source$.pipe(
        map(data => {
          return Object.values(data.reduce((acc, curr) => {
            if (acc[curr.module.source_type]) {
              acc[curr.module.source_type].push(curr);
            }
            else {
              acc[curr.module.source_type] = [curr];
            }
            return acc;
          }, {} as Record<SourceType, {module: Module, value: number, room: Room}[]>));
        }),
      );
    };
  }

  /**
   * Combines the source with the total of the home
   */
  private combineWithHomeTotal() {
    return (source$: Observable<{module: Module, value: number, room: Room}[][]>) => {
      return source$.pipe(
        combineLatestWith(
          combineLatest({
            homeId: this.homesFacade.currentHomeId$,
            state: this.controlPageStore.state$,
          }).pipe(
            switchMap(({homeId, state: {scale, timeRange, homeConsumptionUnit}}) => this.measuresFacade.getTimeRangeMeasuresTotal(
              homeId,
              [homeConsumptionUnit === ConsumptionUnit.CONSUMPTION
                ? EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE
                : EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,],
              this.controlPageStore.scaleToApiScale(scale),
              timeRange,
            ))
          )
        )
      );
    };
  }

  /**
   * Add "rest of home" home fake powerline when the sum does not addup to more
   * than 90% of the home total
   */
  private addRestOfHome() {
    return (source$: Observable<[{module: Module, value: number, room: Room}[][], number]>) => {
      return source$.pipe(
        map(([groups, homeTotal]) => {
          const groupsTotals = groups.map(group => group.reduce((acc, curr) => acc + curr.value, 0));
          const total = groupsTotals.reduce((acc, curr) => acc + curr, 0);
          if (total / homeTotal < 0.95) {
            groups = [...groups, [{
              module: {
                name: PowerLines.rest_of_home.name,
                source_type: PowerLines.rest_of_home.type,
              } as Module,
              value: homeTotal - total,
              room: null,
            }]];
          }
          return [groups, homeTotal];
        }),
      );
    };
  }

  /**
   * Caclculates the total and the ration for each group of appliances
   */
  private addGroupTotalAndRatio() {
    return (source$: Observable<[{module: Module, value: number, room: Room}[][], number]>) => {
      return source$.pipe(
        map(([groups, homeTotal]) => {
          const groupsTotals = groups.map(group => group.reduce((acc, curr) => acc + curr.value, 0));
          return groups.map((group, i) => {
            return {
              rows: group.map(m => ({
                ...m,
                ratio: m.value / groupsTotals[i],
              })),
              value: groupsTotals[i],
              ratio: groupsTotals[i] / homeTotal,
            };
          });
        }),
      );
    };
  }

  private sortByValueFn = (groupA: {value: number}, groupB: {value: number}) => {
    return groupA.value >= groupB.value
      ? groupA.value === groupB.value ? 0 : -1
      : 1;
  };
  private sortByValue() {
    return (source$: Observable<ConsumptionGroup[]>) => {
      return source$.pipe(
        tap(groups => {
          groups.sort(this.sortByValueFn);
          groups.forEach(group => group.rows.sort(this.sortByValueFn));
        }),
      );
    };
  }

  private addColors() {
    return (source$: Observable<ConsumptionGroup[]>) => {
      return source$.pipe(
        tap(groups => {
          const groupGradient = this.creategradient(groups);
          groups.forEach((g, groupIndex) => {
            g.color = groupGradient[groupIndex];

            const rowGradient = this.creategradient(g.rows);
            g.rows.forEach((r, rowIndex) => r.color = rowGradient[rowIndex]);
          });
        }),
      );
    };
  }

  private creategradient(groups: Pick<ConsumptionGroup, 'ratio'>[]) {
    const consumingItems = groups.filter(item => item.ratio > 0);

    const ConsumptionBarsColors = ['#e95e3f', '#f28d27', '#fdc608'];

    if (consumingItems.length === 0) {
      return [];
    }

    return new Gradient()
      .addColorStops(...ConsumptionBarsColors)
      .toArray(2 * consumingItems.length + 1)
      .filter((m, i) => i % 2 === 1);
  }

  private updateSelectedGroup() {
    return (source$: Observable<ConsumptionGroup[]>) => {
      return source$.pipe(
        withLatestFrom(this.selectedPowerlinesGroup.selectedGroup$),
        tap(([groups, selectedGroup]) => {
          if (selectedGroup) {
            const group = groups.find(g => g.id === selectedGroup.id);
            if (group) {
              this.selectedPowerlinesGroup.selectedGroup.next(group);
            }
          }
        }),
        map(([original]) => original),
      );
    };
  }

}
